export const getPercentFromAmount = (amount, total) => {
  amount = amount || 0;
  total = total || 0;

  if (total === 0) {
    return 0;
  }

  return amount / total;
};

export const getAmountFromPercent = (percent, total) => {
  percent = percent || 0;
  total = total || 0;

  return (percent * total) / 100;
};

export const getAmountFromPercentUI = (percent, total) => {
  percent = percent || 0;
  total = total || 0;

  return formatGridAmount(percent * total);
};

export const formatGridAmount = (amount) => {
  if (!amount) {
    return 0;
  }

  if (countDecimals(amount) > 2) {
    return amount.toFixed(2);
  }

  return amount;
};

export const countDecimals = function (value) {
  if (value % 1 !== 0) return value.toString().split(".")[1].length;
  return 0;
};

export const getBenefitTypeOrder = (offerType) => {
  const normalizedOfferType = String(offerType ?? "").toUpperCase();

  switch (normalizedOfferType) {
    case "MED":
      return 0;
    case "DEN":
      return 1;
    case "VIS":
      return 2;
    default:
      return 99;
  }
};

// Kendo's groupBy defaults to sorting resulting groups alphabetically by the
// grouped field's value, regardless of the input array's order. To display
// plan groups in Medical/Dental/Vision order we must supply our own compare
// on the group descriptor rather than relying on pre-sorting `rates`.
//
// The default descriptor is a shared frozen constant so that callers wrapping
// this in `useMemo([data.rates, ...])` get a stable reference across renders
// even when `data.rates` is reassigned. Returning a fresh array here would
// destabilize any effect deps that include the resulting descriptor, and in
// combination with in-place `group.rates = rates` mutations elsewhere in the
// layout, that produces a render loop where the grid data flips between the
// grouped rows and empty on every tick.
const DEFAULT_PLAN_NAME_GROUP_DESCRIPTOR = Object.freeze([
  Object.freeze({ field: "planName" }),
]);

export const buildPlanNameGroupDescriptor = (rates, { orderByBenefitType } = {}) => {
  if (!orderByBenefitType) {
    return DEFAULT_PLAN_NAME_GROUP_DESCRIPTOR;
  }

  const benefitTypeByPlanName = new Map();
  (rates ?? []).forEach((rate) => {
    if (rate?.planName && !benefitTypeByPlanName.has(rate.planName)) {
      benefitTypeByPlanName.set(
        rate.planName,
        getBenefitTypeOrder(rate.planOfferType),
      );
    }
  });

  const compare = (planNameA, planNameB) => {
    const orderA = benefitTypeByPlanName.get(planNameA) ?? 99;
    const orderB = benefitTypeByPlanName.get(planNameB) ?? 99;

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return String(planNameA ?? "").localeCompare(String(planNameB ?? ""));
  };

  return [{ field: "planName", compare }];
};

// SOF-838: a plan on the Carrier Terminated Plans list is shown for historical reference
// only. It is read-only and carries no contribution, which is also what keeps it out of the
// Save Draft payload (onSaveDraft only sends rows with a contribution method selected).
export const isRateCarrierTerminated = (rate) => rate?.isCarrierTerminated === true;

export const setRateGridFields = (newModelDetail) => {
  newModelDetail.data.groups = newModelDetail.data.groups.map((group) => {
    group.rates.map((item) => {
      const carrierTerminated = isRateCarrierTerminated(item);

      item.inEdit = !carrierTerminated;
      item.maxContribution = 0;
      item.currentFmtPercentage = item.currentPercentage / 100;
      item.percentSelected = !carrierTerminated && item.newMethod === "P";
      item.fixedValueSelected = !carrierTerminated && item.newMethod === "F";
      if (item.percentSelected === true || item.fixedValueSelected === true) {
        item.percentValue =
          item.newMethod === "P"
            ? (item.newContributionValue ?? 0) / 100
            : getPercentFromAmount(
                item.newContributionValue,
                item.newTotalPremium
              );
        item.fixedValue =
          item.newMethod === "F"
            ? item.newContributionValue ?? 0
            : getAmountFromPercent(
                item.newContributionValue,
                item.newTotalPremium
              );
        item.newCompanyContribution = item.fixedValue;
        item.newEmployeeContribution = (item.newTotalPremium ?? 0) - (item.fixedValue ?? 0);
      }
      return item;
    });
    return group;
  });
};
